import type { ActivityScope } from "@solution-advisor/context-events";

import { API_PATHS, CONVERSATION_ID_HEADER } from "src/constants/api";

export interface ChatTurnRequest {
	conversationId?: string;
	message: string;
	sessionId: string;
	page: { path: string; title?: string };
	/** The Company › Project › Solution the user is looking at; the API resolves names and the brief. */
	scope?: ActivityScope;
}

interface StreamEvent {
	type?: string;
	payload?: { text?: string };
}

/**
 * Sends one chat turn and yields the assistant's text as it streams. The API relays the AI infra's
 * SSE (`data: {"type":"text-delta","payload":{"text":"…"}}`); other event types are skipped.
 */
export const streamChatTurn = async (
	request: ChatTurnRequest,
	onText: (text: string) => void,
	signal?: AbortSignal
): Promise<{ conversationId: string }> => {
	const response = await fetch(API_PATHS.advisorChat, {
		method: "POST",
		headers: { "content-type": "application/json" },
		credentials: "same-origin",
		body: JSON.stringify(request),
		signal
	});
	if (!response.ok || !response.body) throw new Error(`Advisor unavailable (HTTP ${response.status})`);

	const conversationId = response.headers.get(CONVERSATION_ID_HEADER) ?? request.conversationId ?? "";
	const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
	let buffer = "";

	const handleLine = (line: string) => {
		if (!line.startsWith("data:")) return;
		try {
			const event = JSON.parse(line.slice("data:".length).trim()) as StreamEvent;
			if (event.type === "text-delta" && event.payload?.text) onText(event.payload.text);
		} catch {
			// Not JSON: not a text delta.
		}
	};

	for (;;) {
		const { done, value } = await reader.read();
		if (done) break;
		buffer += value;
		const lines = buffer.split("\n");
		buffer = lines.pop() ?? "";
		lines.forEach(handleLine);
	}
	handleLine(buffer);

	return { conversationId };
};
